import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Search, Download, LogOut, RefreshCw, Loader2, ShieldAlert, ArrowUpDown } from 'lucide-react';
import { usePageMeta } from '../lib/usePageMeta';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

axios.defaults.withCredentials = true;

export default function AdminOnboarding() {
  usePageMeta({ title: 'Admin · Onboarding uploads', robots: 'noindex, nofollow' });
  const navigate = useNavigate();

  const [authState, setAuthState] = useState('checking'); // checking | authed | denied | anon
  const [user, setUser] = useState(null);

  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sortDesc, setSortDesc] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  const login = () => {
    const redirectUrl = window.location.origin + '/admin/onboarding';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await axios.post(`${API_BASE}/auth/logout`); } catch (_) {}
    setUser(null);
    setAuthState('anon');
  };

  // Check current session on mount.
  useEffect(() => {
    // Skip check when returning from OAuth (AuthCallback owns that path).
    if (window.location.hash?.includes('session_id=')) {
      return;
    }
    axios.get(`${API_BASE}/auth/me`)
      .then((r) => {
        if (r.data?.is_admin) {
          setUser(r.data);
          setAuthState('authed');
        } else {
          setUser(r.data);
          setAuthState('denied');
        }
      })
      .catch(() => setAuthState('anon'));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const params = { sort: sortDesc ? 'desc' : 'asc' };
      if (search.trim()) params.q = search.trim();
      const r = await axios.get(`${API_BASE}/admin/uploads`, { params });
      setUploads(r.data?.uploads || []);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || 'Failed to load uploads.');
    } finally {
      setLoading(false);
    }
  }, [search, sortDesc]);

  useEffect(() => {
    if (authState === 'authed') load();
  }, [authState, load]);

  // Debounced search
  useEffect(() => {
    if (authState !== 'authed') return;
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [search, sortDesc, authState, load]);

  const downloadFile = async (upload) => {
    try {
      const r = await axios.get(`${API_BASE}/admin/uploads/${upload.id}/download`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = upload.original_filename || `${upload.id}.bin`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 500);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || 'Download failed.');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch { return iso; }
  };
  const formatSize = (n) => {
    if (typeof n !== 'number') return '-';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  if (authState === 'checking') {
    return <CenteredMessage>Checking session…</CenteredMessage>;
  }
  if (authState === 'anon') {
    return (
      <CenteredMessage>
        <p className="font-serif text-2xl mb-4" style={{ color: '#130F28' }}>Admin sign-in</p>
        <p className="text-sm mb-6" style={{ color: '#3b3656' }}>
          This page is restricted. Sign in with your Google account to continue.
        </p>
        <button onClick={login} data-testid="admin-google-signin"
          className="btn-primary rounded-full px-6 py-3 text-sm font-semibold">
          Sign in with Google
        </button>
      </CenteredMessage>
    );
  }
  if (authState === 'denied') {
    return (
      <CenteredMessage>
        <ShieldAlert size={40} className="mx-auto mb-4" style={{ color: '#a31a1a' }} />
        <p className="font-serif text-2xl mb-3" style={{ color: '#130F28' }}>Access restricted</p>
        <p className="text-sm mb-6" style={{ color: '#3b3656' }}>
          Signed in as <span className="font-semibold">{user?.email || ''}</span>. This account isn't on the allowlist.
        </p>
        <button onClick={logout} className="text-sm underline" style={{ color: '#5132d4' }}>Sign out</button>
      </CenteredMessage>
    );
  }

  return (
    <main id="main" data-testid="admin-onboarding-page" className="py-10 sm:py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="uppercase tracking-[0.16em] text-[11px] font-semibold" style={{ color: '#5132d4' }}>Admin</p>
            <h1 className="font-serif text-3xl sm:text-4xl mt-1" style={{ color: '#130F28' }}>Onboarding uploads</h1>
            <p className="text-sm mt-1" style={{ color: '#4a4566' }}>Signed in as {user?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh"
              className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2 border transition hover:bg-white"
              style={{ borderColor: '#e6e2d6', color: '#130F28' }}>
              <RefreshCw size={14} aria-hidden="true" /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2 border transition hover:bg-white"
              style={{ borderColor: '#e6e2d6', color: '#130F28' }}>
              <LogOut size={14} aria-hidden="true" /> Sign out
            </button>
          </div>
        </div>

        <div className="rounded-2xl bg-white border p-4 sm:p-5" style={{ borderColor: '#e6e2d6' }}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <label className="flex-1 flex items-center gap-2 rounded-full px-4 py-2 border bg-[#faf8f2]" style={{ borderColor: '#e6e2d6' }}>
              <Search size={16} aria-hidden="true" style={{ color: '#4a4566' }} />
              <input
                type="search"
                placeholder="Search by business name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="admin-search"
                className="flex-1 bg-transparent outline-none text-sm"
              />
            </label>
            <button
              onClick={() => setSortDesc((v) => !v)}
              data-testid="admin-sort"
              className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2 border"
              style={{ borderColor: '#e6e2d6', color: '#130F28' }}
              aria-label={`Sort ${sortDesc ? 'oldest' : 'newest'} first`}
            >
              <ArrowUpDown size={14} aria-hidden="true" /> {sortDesc ? 'Newest first' : 'Oldest first'}
            </button>
          </div>

          {errorMsg && (
            <p role="alert" className="mt-3 text-sm" style={{ color: '#a31a1a' }}>{errorMsg}</p>
          )}

          <div className="mt-4 overflow-x-auto -mx-4 sm:mx-0">
            <table className="min-w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: '#fbfaf6' }}>
                  {['Business', 'Email', 'Uploaded', 'File', 'Rows', 'Size', ''].map((c) => (
                    <th key={c} className="text-left px-3 py-2 font-semibold whitespace-nowrap" style={{ color: '#130F28' }}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-sm" style={{ color: '#4a4566' }}>
                    <Loader2 size={16} className="inline animate-spin mr-2" /> Loading…
                  </td></tr>
                )}
                {!loading && uploads.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-sm" style={{ color: '#4a4566' }}>
                    No uploads yet.
                  </td></tr>
                )}
                {!loading && uploads.map((u) => (
                  <tr key={u.id} style={{ borderTop: '1px solid #efece2' }} data-testid={`admin-row-${u.id}`}>
                    <td className="px-3 py-3 whitespace-nowrap font-semibold" style={{ color: '#130F28' }}>{u.business_name}</td>
                    <td className="px-3 py-3 whitespace-nowrap" style={{ color: '#3b3656' }}>{u.email}</td>
                    <td className="px-3 py-3 whitespace-nowrap" style={{ color: '#3b3656' }}>{formatDate(u.created_at)}</td>
                    <td className="px-3 py-3 whitespace-nowrap" style={{ color: '#3b3656' }}>{u.original_filename}</td>
                    <td className="px-3 py-3 whitespace-nowrap" style={{ color: '#3b3656' }}>{u.row_count ?? '-'}</td>
                    <td className="px-3 py-3 whitespace-nowrap" style={{ color: '#3b3656' }}>{formatSize(u.size)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-right">
                      <button
                        onClick={() => downloadFile(u)}
                        data-testid={`admin-download-${u.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border"
                        style={{ borderColor: '#6B4BEF', color: '#5132d4' }}
                      >
                        <Download size={12} aria-hidden="true" /> Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>
  );
}

function CenteredMessage({ children }) {
  return (
    <main className="min-h-[70vh] flex items-center justify-center py-10">
      <div className="max-w-md w-full text-center px-6">{children}</div>
    </main>
  );
}
